import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useQuery } from "@tanstack/react-query";
import {
  FilterBuilder,
  FilterBuilderFieldsType,
  FilterBuilderFilterType,
  Skeleton,
} from "@validereinc/common-components";
import {
  AssetType,
  ReportingGroupAdapter,
  Resources,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { parseBackendFilterToFilterBuilderFormat } from "../Utils/WorkflowConfigurationUpdateRecordStatusDetails.helpers";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationUpdateRecordStatusDetails.module.scss";

const cx = classNames.bind(styles);

const WorkflowConfigurationUpdateRecordStatusDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const {
    setUpdateRecordStatus,
    setUpdateRecordStatusIsValid,
    getUpdateRecordStatusFormValues,
  } = useWorkflowConfigurationFormStore();
  const { getAvailableStepVariables } = useWorkflowGraphState();
  const storeValues = getUpdateRecordStatusFormValues();

  const [filterBuilderFilters, setFilterBuilderFilters] = useState({});

  const onFilterChange = (newFilterValue: FilterBuilderFilterType) => {
    setUpdateRecordStatus({ record_filter: newFilterValue });
    setUpdateRecordStatusIsValid(Object.keys(newFilterValue).length > 0);
  };

  const { data: reportingGroupsData, isLoading: isReportingGroupsLoading } =
    useQuery({
      queryKey: [Resources.REPORTING_GROUP, "getList"],
      queryFn: () => ReportingGroupAdapter.getList({}),
    });
  const reportingGroups = reportingGroupsData?.data ?? [];

  useDeepCompareEffectNoCheck(() => {
    if (!storeValues?.record_filter) return;

    const parseResult = parseBackendFilterToFilterBuilderFormat(
      storeValues.record_filter
    );
    setFilterBuilderFilters(parseResult);
  }, [storeValues?.record_filter]);

  const newFields: FilterBuilderFieldsType = useMemo(() => {
    const draft: FilterBuilderFieldsType = {};
    draft.year_month = {
      data_type: "date",
      display_name: "Period",
      format: "yyyyMM",
    };
    draft["equipment.facility.name"] = {
      data_type: "string",
      display_name: "Equipment Facility Name",
    };
    draft["facility.name"] = {
      data_type: "string",
      display_name: "Facility Name",
    };
    draft.reporting_group_id = {
      data_type: "pick-list",
      display_name: "Reporting Scenario",
      pick_list_options: reportingGroups.map((group) => group.id),
    };
    draft.asset_type = {
      data_type: "pick-list",
      display_name: "Asset Type",
      pick_list_options: Object.values(AssetType),
    };
    return draft;
  }, [reportingGroups]);

  const workflowVariables = getAvailableStepVariables(stepId ?? "");

  return (
    <Skeleton isLoading={isReportingGroupsLoading}>
      <FilterBuilder
        showInputModeToggle
        availableVariables={workflowVariables}
        className={cx("filter-builder")}
        variant="vertical"
        fields={newFields}
        filter={filterBuilderFilters}
        setFilter={setFilterBuilderFilters}
        onFilterChange={onFilterChange}
        maxDepth={2}
      />
    </Skeleton>
  );
};

export default WorkflowConfigurationUpdateRecordStatusDetails;
